/* eslint-disable */

import { ChartOptions, TooltipItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { useMemo, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';

import { htmlLegendPlugin } from 'common/utils/chart';
import KioskChartLegends from 'extends/KioskChartLegends';
import { useTranslation } from 'react-i18next';
import { formatNumberWithSeparators } from 'common/utils/functions';

interface KidneyRiskProps {
  kidneyRisk?: {
    zeroPercent: number;
    missing: number;
    stage_3: number;
    stage_4_5: number;
    twentyPercent: number;
  };
  style?: React.CSSProperties;
}

const KidneyRisk: React.FC<KidneyRiskProps> = ({ kidneyRisk, style }) => {
  const { t } = useTranslation();

  const handlLabelName = (label?: string) => {
    switch (label) {
      case 'zeroPercent':
        return t('dashboard.no');
      case 'missing':
        return t('dashboard.unknow');
      case 'stage_3':
        return t('dashboard.stage_3');
      case 'stage_4_5':
        return t('dashboard.stage_4_5');
      case 'twentyPercent':
        return t('dashboard.twentyPercent');
      default:
        return '';
    }
  };

  const useMemoKidneyRiskData = useMemo(() => {
    if (!kidneyRisk) {
      return {
        total: [] as number[],
        kidneyRisk: [] as string[],
        percentage: [] as string[],
      };
    }
    const kidneyRiskData = [
      kidneyRisk.stage_4_5,
      kidneyRisk.stage_3,
      kidneyRisk.twentyPercent,
      // kidneyRisk.missing,
      kidneyRisk.zeroPercent,
    ];
    const total = kidneyRiskData.reduce((acc, curr) => acc + curr, 0);
    return {
      total: kidneyRiskData,
      kidneyRisk: [
        handlLabelName('stage_4_5'),
        handlLabelName('stage_3'),
        handlLabelName('twentyPercent'),
        // handlLabelName('missing'),
        handlLabelName('zeroPercent'),
      ],
      percentage: kidneyRiskData.map((item) => ((item / total) * 100).toFixed(2)),
    };
  }, [kidneyRisk]);

  const data = {
    datasets: [
      {
        data: useMemoKidneyRiskData.total,
        backgroundColor: ['#052534', '#0B5374', '#1081B6',
          // '#2BA4DB',
          '#9CAACA'],
        borderWidth: 0,
      }
    ],
    labels: useMemoKidneyRiskData.kidneyRisk,
  };

  const chartRef = useRef<any>(null);
  const [hiddens, setHiddens] = useState<boolean[]>(data.labels.map(() => false));

  const options: ChartOptions<'doughnut'> = useMemo(() => ({
    responsive: true,
    cutout: '0%',
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        color: '#fff',
        font: {
          size: 16,
          weight: 600
        },
        formatter: (value: number, context) => {
          if (value === 0) return '';
          const percentage = Math.round(Number(useMemoKidneyRiskData.percentage?.[context.dataIndex]));
          return [percentage > 2 ? `${percentage}%` : ''];
        }
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: TooltipItem<'doughnut'>) => {
            const percentage = useMemoKidneyRiskData.percentage?.[tooltipItem.dataIndex];
            return `${tooltipItem.label}: ${formatNumberWithSeparators(Number(tooltipItem.raw))} (${Math.round(Number(percentage))}%)`;
          },
        },
      },
    }
  }), [useMemoKidneyRiskData]);

  const handleLegendClick = (index: number) => {
    const chart = chartRef.current;

    if (!chart) return;

    // Toggle visibility
    chart.toggleDataVisibility(index);
    chart.update();

    // Update local state (to dim legend item)
    setHiddens((prev) => {
      const newHidden = [...prev];
      newHidden[index] = !newHidden[index];
      return newHidden;
    });
  };

  return (
    <div
      className='p-home_chart'
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px',
        ...style
      }}
    >
      <h1
        style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#3D424F',
          marginBottom: '24px',
          width: '100%'
        }}
      >
        {t('dashboard2025.classifiRisk')}
      </h1>
      <div style={{ width: '366px' }}>
        <Doughnut
          ref={chartRef}
          data={data}
          options={options}
          plugins={[ChartDataLabels, htmlLegendPlugin()]}
        />
      </div>
      <KioskChartLegends
        hiddens={hiddens}
        style={{ marginTop: 20 }}
        labels={data.labels as string[]}
        colors={data.datasets[0].backgroundColor as string[]}
        onClick={handleLegendClick}
      />
    </div>
  );
};

export default KidneyRisk;
